/**
 * E4.8 / M18 — „Beobachtet": das, was MAESTRO über den Betrieb gelernt hat, mit Widerspruchsrecht.
 *
 * Der Widerspruchsknopf ist hier kein Zubehör, sondern der Grund für die ganze Fläche. „Das habe
 * ich über deinen Betrieb gelernt" ist nur dann etwas wert, wenn daneben etwas steht, das es
 * widerlegt — sonst ist es eine Behauptung, die man zur Kenntnis nimmt. Bis zu diesem Kriterium
 * gab es keine einzige Route, die den Status ändern konnte: man konnte lesen und nicken.
 *
 * Und der Widerspruch muss halten: `stimmt_nicht` setzt einen Fakt auf `disputed`, und die
 * Konsolidierung (sql/192) übergeht ihn danach — sie frischt ihn nicht mehr auf. Wer widerspricht
 * und es nachts wiederkommen sieht, widerspricht kein zweites Mal.
 *
 * WISSEN-UMBAU (Antoines Go 05.08.2026, „Drei Fragen statt drei Systembegriffe"): Der eigene
 * Reiter „Beobachtet" ist in den Reiter „Was MAESTRO weiß" (knowledge.tsx) aufgegangen — Fakten,
 * Einsichten und Bausteine stehen dort in EINER Fläche mit Herkunfts-Badges. Diese Datei stellt
 * dafür die unverändert übernommenen Bausteine bereit (Einsicht-Karte samt aufklappbarer Belege,
 * Fakt-Karte, Urteilsknöpfe) — die Widerspruchs-Mechanik bleibt exakt erhalten.
 */
import { useState } from "react";
import { apiFetch } from "../lib/api";
import { Icon, Zeichenzaehler, Textarea } from "../ui/components";

/** S3.5: eine aufgelöste Beleg-Zeile aus GET /api/gedaechtnis/einsicht/:id/belege. */
interface Beleg {
  id: string;
  angeboten: string;
  reagiert: string;
  umstand: Record<string, unknown>;
  createdAt: string;
}

export interface Fakt {
  id: string;
  aussage: string;
  quelle: string;
  konfidenz: number;
  status: string;
  entityKind: string | null;
  createdAt: string;
}
export interface Einsicht {
  id: string;
  art: string;
  titel: string;
  begruendung: string;
  evidenz: { kind?: string; id?: string; note?: string }[];
  konfidenz: number;
  status: string;
  createdAt: string;
}
export type Urteil = "stimmt" | "stimmt_nicht" | "ausblenden";

/** Woher die Aussage stammt — im Klartext. „conversation" sagt einem Betreiber nichts. */
export const QUELLE_TEXT: Record<string, string> = {
  mensch: "von Ihnen gesagt",
  conversation: "im Chat beobachtet",
  action: "aus Ihren Entscheidungen gezählt",
  befund: "aus einer Bestandsprüfung",
  import: "aus einem Import",
};

/** S3.5: die Reaktion in der Beleg-Zeile — im Klartext statt als Datenwert. */
const STATUS_REAGIERT: Record<string, string> = {
  angenommen: "übernommen",
  abgelehnt: "abgelehnt",
  veraendert: "geändert übernommen",
  ignoriert: "nicht beachtet",
};

const STATUS_TEXT: Record<string, string> = {
  confirmed: "bestätigt",
  unverified: "noch unbestätigt",
  disputed: "von Ihnen bestritten",
  angenommen: "bestätigt",
  verworfen: "von Ihnen bestritten",
  offen: "noch unbestätigt",
  // sql/525: Muster aus einem Gast-Absagegrund - wirkt erst nach „Stimmt“.
  vorschlag: "Vorschlag, wirkt erst nach Bestätigung",
};

/**
 * Wissen-Umbau: die Herkunft in Betreiber-Sprache. Drei Kategorien statt fünf Quellwerte —
 * der Betreiber muss nur wissen, ob ER es gesagt hat, ob MAESTRO es beobachtet hat oder ob es
 * aus seinen hochgeladenen Unterlagen stammt.
 */
export type Herkunft = "ihnen" | "beobachtet" | "referenzen";

export function herkunftVonFakt(f: Fakt): Herkunft {
  if (f.quelle === "mensch") return "ihnen";
  // 'import' = aus hochgeladenen Unterlagen/Bestandsdaten übernommen — der Betreiber kennt das
  // als „Referenzen", nicht als „Import".
  if (f.quelle === "import") return "referenzen";
  return "beobachtet";
}

const HERKUNFT_META: Record<Herkunft, { label: string; klasse: string }> = {
  ihnen: { label: "Von Ihnen", klasse: "bg-secondary-container text-on-secondary-container" },
  beobachtet: { label: "Beobachtet", klasse: "bg-primary-container text-on-primary-container" },
  referenzen: { label: "Aus Referenzen", klasse: "bg-tertiary-container text-on-tertiary-container" },
};

export function HerkunftBadge({ herkunft }: { herkunft: Herkunft }) {
  const m = HERKUNFT_META[herkunft];
  return <span className={`rounded-full px-2 py-0.5 font-label-md text-label-md ${m.klasse}`}>{m.label}</span>;
}

export function Urteilsknöpfe({ busy, aktiv, onUrteil }: { busy: boolean; aktiv: string; onUrteil: (u: Urteil) => void }) {
  const knopf = "rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-40";
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={() => onUrteil("stimmt")}
        className={`${knopf} ${aktiv === "confirmed" || aktiv === "angenommen" ? "border-primary bg-primary/10 text-primary" : "border-outline-variant text-on-surface-variant hover:border-primary/40 hover:text-primary dark:border-white/10"}`}
      >
        <Icon name="check" className="mr-1 text-[13px]" />
        Stimmt
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => onUrteil("stimmt_nicht")}
        className={`${knopf} ${aktiv === "disputed" || aktiv === "verworfen" ? "border-outline bg-surface-container-high text-on-background dark:bg-white/[0.08] dark:text-inverse-on-surface" : "border-outline-variant text-on-surface-variant hover:bg-surface-container-high dark:border-white/10"}`}
      >
        <Icon name="close" className="mr-1 text-[13px]" />
        Stimmt nicht
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => onUrteil("ausblenden")}
        className={`${knopf} border-outline-variant text-on-surface-variant hover:text-on-background dark:border-white/10`}
      >
        Ausblenden
      </button>
    </div>
  );
}

/**
 * Eine Einsicht („Muster, das mir aufgefallen ist") samt aufklappbaren Belegen und
 * Urteilsknöpfen — unverändert aus dem früheren Beobachtet-Reiter übernommen. Die Belege werden
 * lazy beim ersten Aufklappen geladen und danach aus dem lokalen State gezeigt.
 */
export function EinsichtKarte({ einsicht: e, busy, onUrteil }: { einsicht: Einsicht; busy: boolean; onUrteil: (u: Urteil) => void }) {
  const [belege, setBelege] = useState<Beleg[] | "laedt" | null>(null);
  const [offen, setOffen] = useState(false);

  const belegeLaden = async () => {
    const jetztOffen = !offen;
    setOffen(jetztOffen);
    if (!jetztOffen || belege) return;
    setBelege("laedt");
    try {
      const res = await apiFetch(`/api/gedaechtnis/einsicht/${e.id}/belege`);
      if (!res.ok) throw new Error("Belege nicht ladbar.");
      const body = (await res.json()) as { data?: { belege?: Beleg[] } };
      setBelege(body.data?.belege ?? []);
    } catch {
      setBelege([]);
    }
  };

  return (
    <div className="rounded-2xl border border-outline-variant bg-surface-container-lowest px-4 py-3.5 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{e.titel}</p>
          <HerkunftBadge herkunft="beobachtet" />
        </div>
        <span className="shrink-0 text-[11px] text-on-surface-variant dark:text-surface-variant">{STATUS_TEXT[e.status] ?? e.status}</span>
      </div>
      <p className="mt-1 text-[12.5px] leading-relaxed text-on-surface-variant dark:text-surface-variant">{e.begruendung}</p>
      {/* Der Beleg ist die halbe Aussage: „auf 7 Vorgänge gestützt" macht aus einer
          Behauptung eine Beobachtung — und macht Widerspruch überhaupt erst begründbar.
          S3.5 (KONZEPT § 6b): ANKLICKBAR — wer „7" liest, kann die sieben sehen. */}
      {e.evidenz.filter((v) => v.id).length > 0 ? (
        <button
          type="button"
          onClick={() => void belegeLaden()}
          className="mt-1 flex items-center text-[11.5px] text-on-surface-variant underline decoration-dotted underline-offset-2 hover:text-on-background dark:text-surface-variant"
        >
          <Icon name="fact_check" className="mr-1 text-[13px] align-[-2px]" />
          auf {e.evidenz.filter((v) => v.id).length} beobachtete Vorgänge gestützt
          <Icon name={offen ? "expand_less" : "expand_more"} className="ml-0.5 text-[14px]" />
        </button>
      ) : (
        <p className="mt-1 text-[11.5px] text-on-surface-variant dark:text-surface-variant">
          <Icon name="fact_check" className="mr-1 text-[13px] align-[-2px]" />
          aus gezählten Vorgängen abgeleitet
        </p>
      )}
      {offen && (
        <div className="mt-2 rounded-xl border border-outline-variant/40 bg-surface px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
          {belege === "laedt" || belege === null ? (
            <p className="text-[11.5px] text-on-surface-variant dark:text-surface-variant">Lädt …</p>
          ) : belege.length === 0 ? (
            <p className="text-[11.5px] text-on-surface-variant dark:text-surface-variant">
              Die zugrunde liegenden Vorgänge sind nicht mehr vorhanden (z.&nbsp;B. bereinigt).
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {belege.map((b) => (
                <li key={b.id} className="text-[11.5px] leading-relaxed text-on-surface-variant dark:text-surface-variant">
                  <span className="font-semibold">{new Date(b.createdAt).toLocaleDateString("de-DE")}</span>
                  {" · "}
                  {b.angeboten}
                  {" — "}
                  <span className="italic">{STATUS_REAGIERT[b.reagiert] ?? b.reagiert}</span>
                  {typeof b.umstand?.grund === "string" && b.umstand.grund ? ` („${b.umstand.grund}")` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <Urteilsknöpfe busy={busy} aktiv={e.status} onUrteil={onUrteil} />
    </div>
  );
}

/**
 * Ein beobachteter/gesagter Fakt mit Herkunfts-Badge und Urteilsknöpfen.
 *
 * `onBearbeiten` ist NUR für vom Betreiber selbst geschriebene Fakten (`quelle==='mensch'`) —
 * Beobachtetem lässt sich weiterhin nur widersprechen, nicht umschreiben. Direktes In-Place-Edit
 * ist auf `tenant_facts` absichtlich nicht erlaubt (sql/185: kein UPDATE/DELETE, RLS gewährt
 * `authenticated` nur INSERT+SELECT — Memory-Poisoning-Schutz, KONZEPT-GEDAECHTNIS § 9). Deshalb
 * ist „Bearbeiten" hier eine KORREKTUR: der alte Fakt wird über den bestehenden Status-Weg
 * ausgeblendet (`retired`, derselbe Weg wie der „Ausblenden"-Knopf), der neue Text entsteht als
 * frischer bestätigter Fakt (`POST /api/gedaechtnis/fakt`). Kein stilles Umschreiben — der
 * erklärende Text unten macht das sichtbar, nicht nur der Code.
 */
export function FaktKarte({
  fakt: f,
  busy,
  onUrteil,
  onBearbeiten,
}: {
  fakt: Fakt;
  busy: boolean;
  onUrteil: (u: Urteil) => void;
  onBearbeiten?: (neuerText: string) => void;
}) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [text, setText] = useState(f.aussage);
  const kannBearbeiten = onBearbeiten != null && f.quelle === "mensch";

  const speichern = () => {
    const t = text.trim();
    if (t.length < 3 || t === f.aussage) return;
    onBearbeiten?.(t);
    setBearbeiten(false);
  };
  const abbrechen = () => {
    setText(f.aussage);
    setBearbeiten(false);
  };

  return (
    <div className="rounded-2xl border border-outline-variant bg-surface-container-lowest px-4 py-3.5 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {bearbeiten ? (
            <span className="text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Aussage korrigieren</span>
          ) : (
            <p className="text-body-sm text-on-background dark:text-inverse-on-surface">{f.aussage}</p>
          )}
          <HerkunftBadge herkunft={herkunftVonFakt(f)} />
        </div>
        {!bearbeiten && <span className="shrink-0 text-[11px] text-on-surface-variant dark:text-surface-variant">{STATUS_TEXT[f.status] ?? f.status}</span>}
      </div>
      {bearbeiten ? (
        <div className="mt-2">
          <Textarea
            className="w-full"
            rows={2}
            maxLength={500}
            value={text}
            onChange={(ev) => setText(ev.target.value)}
            autoFocus
          />
          <Zeichenzaehler current={text.length} max={500} />
          <p className="mt-1 text-[11px] text-on-surface-variant dark:text-surface-variant">
            Das ist eine Korrektur, kein stilles Umschreiben: Die bisherige Aussage wird ausgeblendet, der neue Text als bestätigtes Wissen neu angelegt.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={busy || text.trim().length < 3 || text.trim() === f.aussage}
              onClick={speichern}
              className="rounded-full border border-primary bg-primary/10 px-3 py-1.5 text-[12px] font-semibold text-primary disabled:opacity-40"
            >
              Korrektur speichern
            </button>
            <button
              type="button"
              onClick={abbrechen}
              className="rounded-full border border-outline-variant px-3 py-1.5 text-[12px] font-semibold text-on-surface-variant hover:text-on-background dark:border-white/10"
            >
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-1 text-[11.5px] text-on-surface-variant dark:text-surface-variant">
            {QUELLE_TEXT[f.quelle] ?? f.quelle}
          </p>
          <Urteilsknöpfe busy={busy} aktiv={f.status} onUrteil={onUrteil} />
          {kannBearbeiten && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setBearbeiten(true)}
              className="mt-2 inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline disabled:opacity-40"
            >
              <Icon name="edit" className="text-[16px]" /> Bearbeiten
            </button>
          )}
        </>
      )}
    </div>
  );
}
